// src/pages/ActiveSessions.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  TableFooter,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Divider,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Checkbox,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import HistoryIcon from "@mui/icons-material/History";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import { format, formatDistanceToNow } from "date-fns";
import { request } from "../utils/request";
import { Link as RouterLink } from "react-router-dom";

/**
 * ActiveSessions (merged)
 *
 * - UI & toolbar from the '2nd' file (breadcrumbs, selection, bulk delete, refresh).
 * - Older logins column + dropdown menu from the '1st' file (shows older logins, newest→oldest).
 * - Robust normalization/parsing for many response shapes.
 *
 * Drop-in replace your existing file.
 */

export default function ActiveSessions() {
  const [sessions, setSessions] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // Pagination state (page is 0-based locally; backend expects page=1..N)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  // Search
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // debounce searchText (400ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // Menu state for older-logins menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSessionId, setMenuSessionId] = useState(null);

  // Bulk selection & delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ------------------ helpers for parsing/normalizing responses ------------------
  const parseRespToArray = (resp) => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.rows)) return resp.rows;
    if (resp.success && Array.isArray(resp.data)) return resp.data;
    return [];
  };

  // Convert many possible shapes to an array of timestamp strings (newest first, unique)
  const normalizeLoginTimes = (raw) => {
    if (!raw) return [];
    let arr = [];

    const pushVal = (v) => {
      if (v == null) return;
      // If object contains common mongo $date or date prop
      if (typeof v === "object") {
        if (v.$date) arr.push(String(v.$date));
        else if (v.date) arr.push(String(v.date));
        else {
          // fallback: try createdAt / updatedAt inside object
          if (v.createdAt) arr.push(String(v.createdAt));
          else if (v.updatedAt) arr.push(String(v.updatedAt));
          else arr.push(JSON.stringify(v));
        }
        return;
      }
      if (v instanceof Date) arr.push(v.toISOString());
      else arr.push(String(v));
    };

    if (Array.isArray(raw)) {
      for (const v of raw) pushVal(v);
    } else if (typeof raw === "object") {
      // object may contain multiple fields
      if (raw.loginTime) pushVal(raw.loginTime);
      if (raw.loginTimes) pushVal(raw.loginTimes);
      if (raw.createdAt) pushVal(raw.createdAt);
      if (raw.updatedAt) pushVal(raw.updatedAt);
      // if nothing pushed, stringify
      if (arr.length === 0) pushVal(raw);
    } else {
      pushVal(raw);
    }

    // sanitize and unique
    arr = arr
      .map((x) => (x == null ? null : String(x)))
      .filter(Boolean)
      .map((x) => {
        // try to convert numeric epoch to ISO
        if (/^\d{10,}$/.test(x)) {
          const n = Number(x);
          try {
            return new Date(n).toISOString();
          } catch {
            return x;
          }
        }
        return x;
      });

    // dedupe while preserving order
    const seen = new Set();
    const deduped = [];
    for (const a of arr) {
      if (!seen.has(a)) {
        seen.add(a);
        deduped.push(a);
      }
    }

    // sort newest first when possible
    deduped.sort((a, b) => {
      const ta = new Date(a).getTime();
      const tb = new Date(b).getTime();
      if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
      return tb - ta;
    });

    return deduped;
  };

  // Merge/normalize session shape (robust)
  const normalizeSession = (s) => {
    const id = s && (s._id || s.id) ? String(s._id || s.id) : null;
    const sessionId = s.sessionId || s.session_id || s.session || "";
    // collect any candidate fields for logins
    const loginCandidates = [].concat(
      s.loginTime ?? [],
      s.loginTimes ?? [],
      s.login_time ?? [],
      s.login_times ?? [],
      s.createdAt ? [s.createdAt] : [],
      s.created_at ? [s.created_at] : []
    );
    const loginTimes = normalizeLoginTimes(loginCandidates.length ? loginCandidates : s.loginTime ?? s.loginTimes ?? s);
    const latest = loginTimes.length > 0 ? loginTimes[0] : null;

    const userId = s.userId || s.user_id || s.user || null;
    const ip = s.ip || s.clientIp || "";
    const deviceId =
      (s.devicePosture && (s.devicePosture.deviceId || s.devicePosture.deviceID)) ||
      s.deviceId ||
      (s.device_posture && (s.device_posture.deviceId || s.device_posture.deviceID)) ||
      "";
    const expiresAt = s.expiresAt || s.expires_at || null;

    return {
      _id: id,
      sessionId,
      loginTime: latest,
      loginTimes,
      userId: userId ? String(userId) : null,
      ip,
      deviceId,
      expiresAt,
      raw: s,
    };
  };
  // ------------------------------------------------------------------------------

  // fetch users once to build map userId -> display name
  const fetchUsers = useCallback(async () => {
    try {
      const usersResp = await request("user", { method: "GET" });
      const usersArray = parseRespToArray(usersResp);
      const uMap = new Map();
      for (const u of usersArray) {
        const id = u && (u._id || u.id) ? String(u._id || u.id) : null;
        if (!id) continue;
        const fname = u.first_name || "";
        const lname = u.last_name || "";
        const username = u.username || "";
        const display = [fname, lname].filter(Boolean).join(" ") || username || id;
        uMap.set(id, display);
      }
      setUsersMap(uMap);
    } catch (err) {
      console.error("ActiveSessions: failed to fetch users:", err);
    }
  }, []);

  // Fetch page of sessions from backend (supports server-side search or client-side fallback)
  const fetchSessionsPage = useCallback(
    async (pageIndex = 0, limit = 25, search = "") => {
      setLoading(true);
      try {
        const backendPage = Math.max(1, pageIndex + 1); // backend expects 1-based page

        // If search is active, try server-side search with larger limit (attempt)
        if (search) {
          try {
            const path = `sessions?page=1&limit=1000&search=${encodeURIComponent(search)}`;
            const resp = await request(path, { method: "GET" });
            const arr = parseRespToArray(resp);
            // server returned something usable
            if (Array.isArray(arr) && arr.length > 0) {
              const normalized = arr.map((s) => normalizeSession(s));
              // apply client-side safety filter too (sessionId OR username)
              const t = search.toLowerCase();
              const filtered = normalized.filter((s) => {
                const sessionMatch = (s.sessionId || "").toLowerCase().includes(t);
                const userName = s.userId ? (usersMap.get(s.userId) || "").toLowerCase() : "";
                const userMatch = userName.includes(t);
                return sessionMatch || userMatch;
              });
              setSessions(filtered.slice(0, 1000)); // cap
              setTotalRows(filtered.length);
              setLastFetchedAt(new Date());
              return;
            }
            // otherwise fallthrough to client-side fetch below
          } catch (e) {
            console.debug("Server-side search failed, falling back to client-side search.", e?.message || e);
          }

          // client-side fallback: fetch a reasonably large page and filter locally
          const fallbackPath = `sessions?page=1&limit=1000`;
          const fallbackResp = await request(fallbackPath, { method: "GET" });
          const fallbackArr = parseRespToArray(fallbackResp);
          const normalized = fallbackArr.map((s) => normalizeSession(s));
          const t = search.toLowerCase();
          const filtered = normalized.filter((s) => {
            const sessionMatch = (s.sessionId || "").toLowerCase().includes(t);
            const userName = s.userId ? (usersMap.get(s.userId) || "").toLowerCase() : "";
            const userMatch = userName.includes(t);
            return sessionMatch || userMatch;
          });
          setSessions(filtered.slice(0, 1000));
          setTotalRows(filtered.length);
          setLastFetchedAt(new Date());
          return;
        }

        // No search -> normal page fetch
        const path = `sessions?page=${backendPage}&limit=${limit}`;
        const resp = await request(path, { method: "GET" });

        // If backend returned wrapper { success: true, data: [...], meta: { total, page, limit } }
        if (resp && typeof resp === "object" && Array.isArray(resp.data) && resp.meta) {
          const normalized = resp.data.map((s) => normalizeSession(s));
          setSessions(normalized);
          const total =
            Number(resp.meta.total ?? resp.meta.totalCount ?? resp.meta.count ?? resp.meta.totalDocs ?? resp.total ?? 0);
          setTotalRows(total);
        } else {
          // fallback: treat resp as array or { data: [...] }
          const arr = parseRespToArray(resp);
          const normalized = arr.map((s) => normalizeSession(s));
          setSessions(normalized);
          setTotalRows(arr.length || 0);
        }

        setLastFetchedAt(new Date());
      } catch (err) {
        console.error("ActiveSessions: failed to fetch sessions:", err);
        setSessions([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    },
    [usersMap]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // fetch when page/rowsPerPage change OR when debouncedSearch changes
  useEffect(() => {
    // If searching, always load page 0 of search results (handled inside fetchSessionsPage)
    if (debouncedSearch) {
      setPage(0);
      fetchSessionsPage(0, rowsPerPage, debouncedSearch);
    } else {
      fetchSessionsPage(page, rowsPerPage, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0); // reset to first page
  };

  const renderStatusChip = (expiresAt) => {
    const active = !expiresAt;
    return active ? (
      <Chip label="Active" size="small" sx={{ backgroundColor: "#e6f4ea", color: "#0b7a3d" }} />
    ) : (
      <Chip label="Inactive" size="small" sx={{ backgroundColor: "#fdecea", color: "#b71c1c" }} />
    );
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return format(d, "yyyy-MM-dd HH:mm:ss");
    } catch {
      return String(iso);
    }
  };

  const refresh = async () => {
    await fetchUsers();
    await fetchSessionsPage(page, rowsPerPage, debouncedSearch);
  };

  // Effective total for pagination
  const effectiveTotal = useMemo(() => totalRows || 0, [totalRows]);

  // ---------------- selection / bulk ----------------
  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = sessions.length > 0 && selectedIds.length === sessions.length;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(sessions.map((s) => s._id || s.sessionId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => {
          // prefer REST path sessions/:id
          return request(`sessions/${id}`, { method: "DELETE" }).catch((e) => {
            // swallow per-item errors so other deletes can continue
            console.error("Failed to delete session", id, e);
          });
        })
      );
      setSelectedIds([]);
      await fetchSessionsPage(page, rowsPerPage, debouncedSearch);
      handleCancelBulkDelete();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      setBulkDeleting(false);
    }
  };
  // ------------------------------------------------------------------------------

  // Menu handlers for older logins
  const openMenuFor = (event, sessionId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSessionId(sessionId);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuSessionId(null);
  };

  const getLoginTimesForSession = (s) => {
    if (!s) return [];
    if (Array.isArray(s.loginTimes) && s.loginTimes.length) return s.loginTimes;
    if (s.loginTime) return [s.loginTime];
    return [];
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        {/* breadcrumbs */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "0 1 auto" }}>
          <RouterLink
            to="/home"
            style={{
              textDecoration: "none",
              color: "#2d9cdb",
              fontSize: "0.95rem",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#0b1d51")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#2d9cdb")}
          >
            Dashboard
          </RouterLink>

          <Typography sx={{ fontSize: "0.95rem" }}>/</Typography>

          <Typography sx={{ fontSize: "0.95rem", color: "black" }}>Active Sessions</Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: "1 1 auto", justifyContent: "flex-end" }}>
          <TextField
            size="small"
            placeholder="Search by sessionId or username"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              "& input::placeholder": {
                color: "black",
                fontWeight: 300,
                opacity: 1,
              },
            }}
          />

          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Toolbar shown when rows selected (just above the table) */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography variant="body2">{selectedIds.length} selected</Typography>

          <Button
            size="small"
            variant="contained"
            onClick={handleDeleteSelected}
            sx={{ backgroundColor: "#e53935", "&:hover": { backgroundColor: "#b71c1c" }, color: "#fff", textTransform: "none" }}
          >
            Delete
          </Button>

          <Button size="small" onClick={() => setSelectedIds([])} sx={{ textTransform: "none" }}>
            Clear
          </Button>
        </Box>
      )}

      <Paper elevation={2}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f5f7fb" }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={isAllSelected} indeterminate={selectedIds.length > 0 && !isAllSelected} onChange={(e) => toggleSelectAll(e.target.checked)} />
                </TableCell>
                <TableCell>#</TableCell>
                <TableCell>Session ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Device ID</TableCell>
                <TableCell>Login Time</TableCell>
                <TableCell>Older Logins</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    No sessions found.
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((s, idx) => {
                  const rowNumber = page * rowsPerPage + idx + 1;
                  const loginTimes = getLoginTimesForSession(s);
                  const latest = loginTimes.length > 0 ? loginTimes[0] : null;
                  // olderTimes: show only older entries (exclude latest)
                  const olderTimes = loginTimes.length > 1 ? loginTimes.slice(1) : [];

                  return (
                    <TableRow key={s._id || `${s.sessionId}-${idx}`} hover selected={selectedIds.includes(s._id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedIds.includes(s._id)} onChange={() => toggleSelect(s._id)} />
                      </TableCell>

                      <TableCell>{rowNumber}</TableCell>

                      <TableCell
                        sx={{
                          maxWidth: 240,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Typography variant="body2" noWrap>
                          {s.sessionId || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{s.userId ? (usersMap.get(s.userId) || s.userId) : "—"}</Typography>
                      </TableCell>

                      <TableCell>{s.ip || "—"}</TableCell>
                      <TableCell>{s.deviceId || "—"}</TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={0}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fmt(latest)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {latest ? formatDistanceToNow(new Date(latest), { addSuffix: true }) : "—"}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {olderTimes.length > 0 ? (
                          <Tooltip title={`Show ${olderTimes.length} older login(s)`}>
                            <IconButton
                              size="small"
                              aria-controls={menuSessionId === (s._id || s.sessionId) && Boolean(menuAnchorEl) ? `login-times-menu-${s._id || s.sessionId}` : undefined}
                              aria-haspopup="true"
                              aria-expanded={menuSessionId === (s._id || s.sessionId) && Boolean(menuAnchorEl) ? "true" : undefined}
                              onClick={(e) => openMenuFor(e, s._id || s.sessionId)}
                              sx={{
                                border: "1px solid rgba(0,0,0,0.08)",
                                bgcolor: "background.paper",
                                "&:hover": { boxShadow: 3 },
                                ml: 0.5,
                              }}
                            >
                              <MoreHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>{renderStatusChip(s.expiresAt)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>

            <TableFooter>
              <TableRow
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 3,
                  backgroundColor: (theme) => theme.palette.background.paper,
                }}
              >
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  colSpan={9}
                  count={effectiveTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page"
                  sx={{
                    "& .MuiTablePagination-toolbar": {
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiTablePagination-selectLabel": {
                      marginBottom: 0,
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiTablePagination-input": {
                      marginTop: 0,
                      marginBottom: 0,
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      marginBottom: 0,
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      {/* Older-login menu (shared, shows entries for selected session) */}
      <Menu
        id={menuSessionId ? `login-times-menu-${menuSessionId}` : "login-times-menu"}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && Boolean(menuSessionId)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            maxHeight: 360,
            minWidth: 320,
            borderRadius: 2,
            boxShadow: 6,
            p: 1,
          },
        }}
      >
        {/* header */}
        <ListItem dense sx={{ pointerEvents: "none", mb: 0.5 }}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <HistoryIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={<Typography variant="subtitle2">Older logins</Typography>}
            secondary={<Typography variant="caption" color="text.secondary">Showing entries older than the latest (newest → oldest)</Typography>}
          />
        </ListItem>

        <Divider sx={{ my: 0.5 }} />

        {/* menu items: find the session by id and list its olderTimes */}
        {(() => {
          if (!menuSessionId) return null;
          const s = sessions.find((it) => (it._id || it.sessionId) === menuSessionId);
          if (!s) return (
            <MenuItem dense>
              <Typography variant="body2">No entries</Typography>
            </MenuItem>
          );
          const loginTimes = getLoginTimesForSession(s);
          const older = loginTimes.length > 1 ? loginTimes.slice(1) : [];
          if (older.length === 0) {
            return (
              <MenuItem dense>
                <Typography variant="body2">No older logins</Typography>
              </MenuItem>
            );
          }
          return older.map((lt, i) => {
            // position (2 means the second-most-recent)
            const position = i + 2;
            let relative = "—";
            try {
              relative = formatDistanceToNow(new Date(lt), { addSuffix: true });
            } catch {
              /* ignore */
            }
            return (
              <MenuItem
                key={`${menuSessionId}-older-${i}`}
                dense
                sx={{
                  alignItems: "flex-start",
                  py: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "grey.100", color: "text.primary", width: 34, height: 34 }}>
                    <HistoryIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{fmt(lt)}</Typography>
                      <Chip size="small" label={`#${position}`} sx={{ ml: 1, bgcolor: "grey.100", fontWeight: 600 }} />
                    </Stack>
                  }
                  secondary={<Typography variant="caption" color="text.secondary">{relative}</Typography>}
                />
              </MenuItem>
            );
          });
        })()}
      </Menu>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteOpen} onClose={handleCancelBulkDelete}>
        <DialogTitle>Delete Selected Sessions</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the following {selectedIds.length} session(s)?</Typography>
          <Box sx={{ mt: 1 }}>
            {selectedIds.map((id) => {
              const it = sessions.find((it) => it._id === id);
              return (
                <Typography key={id} variant="body2">
                  • {it ? it.sessionId : id}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBulkDelete} disabled={bulkDeleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmBulkDelete} color="error" variant="contained" disabled={bulkDeleting}>
            {bulkDeleting ? "Deleting..." : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}